import { useEffect, useState, useRef } from 'react';
import { services } from '../../../mocks/services';

interface ServiceOrderModalProps {
  selectedServiceId: number | null;
  onClose: () => void;
  planName?: string;
  planPrice?: string;
}

type FormStatus = 'idle' | 'sending' | 'success' | 'error';

const FORM_URL = 'https://readdy.ai/api/form/d735c24lqq77n94m7c40';

export default function ServiceOrderModal({ selectedServiceId, onClose, planName, planPrice }: ServiceOrderModalProps) {
  const [serviceId, setServiceId] = useState<number>(selectedServiceId ?? services[0].id);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', gdpr: false });
  const [status, setStatus] = useState<FormStatus>('idle');
  const [charCount, setCharCount] = useState(0);
  const overlayRef = useRef<HTMLDivElement>(null);

  const selectedService = services.find((s) => s.id === serviceId) ?? services[0];

  // Sync service when prop changes
  useEffect(() => {
    if (selectedServiceId !== null) setServiceId(selectedServiceId);
  }, [selectedServiceId]);

  // Lock scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // ESC key to close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setForm((prev) => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      if (name === 'message') setCharCount(value.length);
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.gdpr || charCount > 500) return;
    setStatus('sending');
    try {
      const bodyData: Record<string, string> = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        service: selectedService.title,
        message: form.message,
      };
      if (planName) bodyData.plan = `${planName}${planPrice ? ` (${planPrice})` : ''}`;
      const body = new URLSearchParams(bodyData);
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setStatus(res.ok ? 'success' : 'error');
      if (res.ok) {
        setForm({ name: '', email: '', phone: '', message: '', gdpr: false });
        setCharCount(0);
      }
    } catch {
      setStatus('error');
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) onClose();
  };

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-950/80 backdrop-blur-sm"
      onClick={handleOverlayClick}
    >
      <div className="relative w-full max-w-lg bg-white rounded-2xl overflow-hidden animate-[slideUp_0.25s_ease-out]">

        {/* Header */}
        <div className={`bg-gradient-to-br ${selectedService.color} border-b border-zinc-100 px-7 py-6`}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 flex items-center justify-center bg-white rounded-xl flex-shrink-0">
                <i className={`${selectedService.icon} text-2xl ${selectedService.accent}`} />
              </div>
              <div>
                <p className="text-zinc-500 text-xs font-medium mb-0.5">Objednávka služby</p>
                <h2 className="text-zinc-900 font-black text-xl leading-tight">{selectedService.title}</h2>
                {planName && (
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className="inline-flex items-center gap-1 bg-white/70 border border-zinc-200 rounded-full px-2.5 py-0.5 text-xs font-semibold text-zinc-700">
                      <i className="ri-price-tag-3-line text-orange-500" />
                      {planName}
                      {planPrice && (
                        <span className="text-orange-500 font-bold ml-1">{planPrice}</span>
                      )}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-all duration-200 cursor-pointer flex-shrink-0 mt-0.5"
              aria-label="Zatvoriť"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>

          {/* Service selector */}
          <div className="mt-5">
            <label className="text-zinc-600 text-xs font-semibold mb-1.5 block">Zmeniť službu</label>
            <select
              value={serviceId}
              onChange={(e) => setServiceId(Number(e.target.value))}
              className="w-full bg-white border border-zinc-200 rounded-xl px-4 py-2.5 text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-orange-400/30 focus:border-orange-400 transition-all duration-200 cursor-pointer"
            >
              {services.map((s) => (
                <option key={s.id} value={s.id}>{s.title}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Body */}
        <div className="px-7 py-6 max-h-[60vh] overflow-y-auto">
          {status === 'success' ? (
            <div className="flex flex-col items-center justify-center text-center py-8">
              <div className="w-14 h-14 flex items-center justify-center bg-emerald-100 rounded-full mb-4">
                <i className="ri-check-line text-emerald-600 text-2xl" />
              </div>
              <h3 className="text-zinc-900 text-xl font-black mb-2">Odoslané!</h3>
              <p className="text-zinc-500 text-sm mb-6">
                Ďakujeme za záujem o <strong>{planName ?? selectedService.title}</strong>. Ozveme sa do 24 hodín.
              </p>
              <button
                onClick={onClose}
                className="bg-orange-500 hover:bg-orange-400 text-white font-semibold px-6 py-2.5 rounded-xl text-sm transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                Zatvoriť
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              id={`service-order-form-${serviceId}`}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-zinc-700 text-xs font-semibold mb-1.5 block">Meno a priezvisko *</label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    placeholder="Ján Novák"
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-2.5 text-sm text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-orange-400/30 focus:border-orange-400 transition-all duration-200"
                  />
                </div>
                <div>
                  <label className="text-zinc-700 text-xs font-semibold mb-1.5 block">Telefón</label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="+421 9xx xxx xxx"
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-2.5 text-sm text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-orange-400/30 focus:border-orange-400 transition-all duration-200"
                  />
                </div>
              </div>

              <div>
                <label className="text-zinc-700 text-xs font-semibold mb-1.5 block">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  placeholder="jan@firma.sk"
                  className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-2.5 text-sm text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-orange-400/30 focus:border-orange-400 transition-all duration-200"
                />
              </div>

              <input type="hidden" name="service" value={selectedService.title} />
              {planName && (
                <input type="hidden" name="plan" value={`${planName}${planPrice ? ` (${planPrice})` : ''}`} />
              )}

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-zinc-700 text-xs font-semibold">Správa / Popis projektu *</label>
                  <span className={`text-xs ${charCount > 450 ? 'text-red-500' : 'text-zinc-400'}`}>
                    {charCount}/500
                  </span>
                </div>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={4}
                  placeholder={planName
                    ? `Popíšte váš zámer pre plán ${planName}...`
                    : `Popíšte váš zámer pre službu ${selectedService.title}...`}
                  className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-3 text-sm text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-orange-400/30 focus:border-orange-400 transition-all duration-200 resize-none"
                />
              </div>

              {/* GDPR */}
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="relative mt-0.5 flex-shrink-0">
                  <input
                    type="checkbox"
                    name="gdpr"
                    checked={form.gdpr}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-all duration-200 ${form.gdpr ? 'bg-orange-500 border-orange-500' : 'bg-white border-zinc-300 group-hover:border-zinc-400'}`}>
                    {form.gdpr && <i className="ri-check-line text-white text-xs" />}
                  </div>
                </div>
                <p className="text-zinc-500 text-xs leading-relaxed">
                  Súhlasím so spracovaním osobných údajov podľa{' '}
                  <a href="/ochrana-sukromia" target="_blank" className="text-orange-500 hover:underline">
                    zásad ochrany súkromia
                  </a>
                  .
                </p>
              </label>

              {status === 'error' && (
                <div className="bg-red-50 border border-red-100 text-red-600 text-xs px-4 py-3 rounded-xl">
                  Odosielanie zlyhalo. Skúste to znova.
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'sending' || !form.gdpr}
                className="w-full bg-orange-500 hover:bg-orange-400 disabled:bg-zinc-200 disabled:text-zinc-400 text-white font-bold py-3.5 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap text-sm"
              >
                {status === 'sending' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    Odosielam...
                  </>
                ) : (
                  <>
                    Odoslať dopyt
                    <i className="ri-send-plane-line" />
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
